import styled from "styled-components";
import { focusOutlineRule } from "styles/focus";
import done from "../Icon/icons/checkbox-select.svg";
import PreLoader from "../PreLoader/PreLoader";
import { srOnly } from "../SrOnly";

interface CheckboxLabelProps {
  $invalid: boolean;
  onClick?: (e: React.MouseEvent<HTMLLabelElement, MouseEvent>) => void;
}

interface CheckboxCustomProps {
  disabled: boolean;
  $customPaddingValue?: number;
}

export const Spinner = styled(PreLoader)`
  position: absolute;
  width: 20px;
  height: 14px;
  transform-origin: 0 10px;
  transform: scale(0.3);
  left: initial;
  right: 100%;
  top: -7px;
  margin-right: 4px;
`;

// Visually hidden, not display: none, so the native input stays focusable and
// in the accessibility tree; CheckboxCustom draws it.
export const CheckboxField = styled.input`
  ${srOnly}
`;

export const CheckboxText = styled.div`
  line-height: 18px;
  margin-left: 8px;
`;

export const CheckboxLabel = styled.label<CheckboxLabelProps>`
  display: inline-flex;
  align-items: center;
  position: relative;
  color: ${(props) => props.theme.black50};

  ${CheckboxField}:checked ~ div${CheckboxText} {
    color: ${(props) => props.theme.black};
  }

  ${({ theme, $invalid }) => ($invalid ? `color: ${theme.red};` : "")}
`;

export const CheckboxLine = styled.label`
  display: inline-flex;
  align-items: center;
`;

export const CheckboxCustom = styled.div<CheckboxCustomProps>`
  position: relative;
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 2px;
  ${({ theme, disabled, $customPaddingValue }) => `
    cursor: ${disabled ? "default" : "pointer"};
    border: 1.25px solid ${disabled ? theme.grey : theme.formBorder};
    background-color: ${disabled ? theme.black05 : theme.surface};
    box-shadow: none;
    transition: all 0.2s ease;
    ${$customPaddingValue ? `margin: 0 ${$customPaddingValue}px;` : ""}
  `}

  ${({ $customPaddingValue }) => `
    &::before {
      content: '';
      width: 20px;
      height: 20px;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      ${$customPaddingValue ? `padding: ${$customPaddingValue}px;` : ""}
    }
  `}

  ${({ theme, disabled }) =>
    !disabled &&
    `
    &:hover {
      border-color: ${theme.primary};
    }
  `}

  ${CheckboxField}:checked + & {
    ${({ theme, disabled }) =>
      !disabled
        ? `
        background-color: ${theme.primary};
        border: 1.25px solid ${theme.primary};
      `
        : `
        background-color: ${theme.modalBorderColor};
      `}
  }

  ${CheckboxField}:checked + &:hover {
    ${({ theme, disabled }) =>
      !disabled &&
      `
      background-color: ${theme.primaryDark};
      border-color: ${theme.primaryDark};
    `}
  }

  ${CheckboxField}:checked + &::before {
    background-image: url("${done}");
    background-repeat: no-repeat;
    ${({ $customPaddingValue }) =>
      $customPaddingValue
        ? `background-position: ${$customPaddingValue}px;`
        : ""}
  }

  ${CheckboxField}:focus-visible + & {
    border-color: ${({ theme }) => theme.primary};
    ${focusOutlineRule}
  }
`;

export const CheckboxIcon = styled.span`
  display: flex;
  margin-left: 16px;
`;
